import React from 'react';
import { isEditorActive, RichText, Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { CHModelConfigurationModel } from './models/productModelConfigurations.model';
import { TCHModelConfigurationInnerComponent, TCHModelConfigurationProps } from './types/productModelConfigurations.types';
import ModelConfigurationCard from './components/modelConfigurationCard';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';

const ProductModelConfigurations = (props: TCHModelConfigurationProps): JSX.Element | null => {
  const apiResponse = isEmptyResponse(props);
  const model = new CHModelConfigurationModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent
      rendering={props?.rendering}
      title={model?.title?.value ?? model?.eyebrow?.value ?? ''}
      template="Model Configuration"
    >
      <section className="product-model-configuration" style={{ backgroundColor: model.backgroundColor || '#fff' }}>
        <section className="product-model-configuration__headings">
          <Text field={model.eyebrow} tag="p" editable={false} className="product-model-configuration__eyebrow" />
          <header>
            <Text tag="h2" field={model.title} editable={false} className="product-model-configuration__title" />
          </header>
          <RichText tag="div" field={model.description} editable={false} className="product-model-configuration__description rich-text" />
        </section>

        <section className="product-model-configuration__cards-wrapper">
          {model?.innerComponents?.map((innerComp: TCHModelConfigurationInnerComponent) => (
            <ModelConfigurationCard {...innerComp} key={innerComp.id} />
          ))}
        </section>
      </section>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductModelConfigurations);
